import type { GameViewProps } from '../../types';

import * as React from 'react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useRouter } from 'next/router';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { useEditorHandlers } from './@editor';
import { useAppContext } from '../../contexts/AppContext';
import { useTheme, darken } from '@mui/material/styles';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { Typography as MuiTypography, useMediaQuery } from '@mui/material';
import { adsRequest } from '../../utils/ads-request';
import { truncateString } from '../../utils/truncate-string';
import { ImageLoaderWrapper } from '../../components/ImageLoaderWrapper';
import { PaperColorElevation } from '../../components/PaperColorElevation';
import { Title } from '../../components/Title';
import { BreadCrumbs } from '../../components/BreadCrumbs';
import Button from '@mui/material/Button';
import ShareIcon from '@mui/icons-material/Share';
import CodeIcon from '@mui/icons-material/Code';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { BrowserView, MobileView, isMobile } from 'react-device-detect';
//import UAgent from '../../utils/u-agent';
import { GameHeaderLayoutEnum, GameLayoutAreaEnum, LayoutTypeEnum } from '../../types/enum';

const GameFrame = dynamic<any>(() => import('../../components/GameFrame').then((mod) => mod.GameFrame), {
  ssr: false,
});
const AdvertisementSlot = dynamic<any>(
  () => import('../../components/AdvertisementSlot').then((mod) => mod.AdvertisementSlot),
  {
    ssr: false,
  }
);
const CollectionSection = dynamic<any>(
  () => import('../../components/CollectionSection').then((mod) => mod.CollectionSection),
  {
    ssr: false,
  }
);
const CarouselList = dynamic<any>(() => import('../../components/CarouselList').then((mod) => mod.CarouselList), {
  ssr: false,
  // suspense: true,
});

interface GameHeaderProps {
  headerLayout?: GameHeaderLayoutEnum;
}

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const GameDesktop = styled(PaperColorElevation)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  width: '100%',
}));

const GameMobile = styled(PaperColorElevation)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  position: 'relative',
  padding: theme.spacing(2, 2),
  margin: theme.spacing(0),
}));

const GameHeader = styled('div')<GameHeaderProps>(({ theme, headerLayout }) => {
  let styles = {};
  if (headerLayout === GameHeaderLayoutEnum.FULL) {
    styles = {
      [theme.breakpoints.up('sm')]: {
        height: '90px',
      },
    };
  }

  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: 0,
    padding: theme.spacing(1.5),
    ...styles,
  };
});

const GameHeaderMeta = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
  padding: theme.spacing(0, 0, 0, 2),
  // overflow: 'hidden',
  '& > h1': {
    [theme.breakpoints.up('sm')]: {
      textAlign: theme.direction === 'rtl' ? 'right' : 'left',
      margin: theme.spacing(0),
    },
  },
  '& > span': {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: darken(theme.palette.text.primary, 0.05),
      minWidth: '0',
    },
  },
}));

const GameHeaderImage = styled('div')<GameHeaderProps>(({ theme, headerLayout }) => {
  let styles = {};
  if (headerLayout === GameHeaderLayoutEnum.FULL) {
    styles = {
      width: 65,
      height: 65,
    };
  } else {
    styles = {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
        float: 'left',
        width: 30,
        height: 30,
      },
    };
  }

  return styles;
});

const AdvertisementSlotStyled = styled(AdvertisementSlot)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  width: 'fit-content',
  margin: 'auto',
}));

const AdvertisementSlotContainer = styled(PaperColorElevation)(({ theme }) => ({
  padding: 0,
  boxShadow: theme.shadows[0],
}));

const Section = styled(PaperColorElevation)(({ theme }) => ({
  position: 'relative',
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
  color: theme.theme_component_game_description_font_color || theme.palette.secondary.light,
  backgroundColor: theme.theme_component_game_description_background_color || theme.palette.secondary.dark,
}));

const DescriptionTitle = styled(Title)(({ theme }) => ({
  color: theme.theme_component_game_description_font_color || theme.palette.secondary.light,
  backgroundColor: theme.theme_component_game_description_background_color || theme.palette.secondary.dark,
  h1: {
    color: theme.theme_component_game_description_font_color || theme.palette.secondary.light,
    backgroundColor: theme.theme_component_game_description_background_color || theme.palette.secondary.dark,
  },
}));

export const GameA = (props: GameViewProps) => {
  const { game, global, pageContext } = props;
  const { t } = useTranslation('game_a');
  const theme = useTheme();
  const router = useRouter();
  const { ref, inView } = useInView();
  const appContext = useAppContext();
  const { assetUrl, componentOptions, pageOptions } = appContext.useConfig();
  const gamePageLayout = pageOptions.find((page) => page.name === 'game')?.layouts?.[0]?.imports?.[0]?.component;
  // @ts-ignore
  //const [uAgent] = React.useState(new UAgent({ debug: false }));
  const [gameRunning, setGameRunning] = React.useState(isMobile);
  const [description, setDescription] = React.useState('');
  const [domLoaded, setDomLoaded] = React.useState(false);
  const [descriptionIsTruncated, setDescriptionIsTruncated] = React.useState<boolean>(true);
  const descriptionTruncateLength = 350;
  const md = useMediaQuery(theme.breakpoints.up('md'));

  const swiperOptions = {
    breakpoints: {
      900: {
        slidesPerView: 4,
        slidesPerGroup: 4,
      },
      600: {
        slidesPerView: 3,
        slidesPerGroup: 3,
      },
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2,
      },
    },
  };

  const publisherId = componentOptions.component_walk_through_publisher_id;
  const layout =
    gamePageLayout === 'ConservativeHome'
      ? LayoutTypeEnum.CONSERVATIVE
      : gamePageLayout === 'ModerateHome'
      ? LayoutTypeEnum.MODERATE
      : gamePageLayout === 'AggressiveHome'
      ? LayoutTypeEnum.AGGRESSIVE
      : LayoutTypeEnum.DEFAULT;

  const enableShare =
    typeof componentOptions.component_share_enable_web_api === 'boolean'
      ? componentOptions.component_share_enable_web_api
      : true;
  const enableClipBoard =
    typeof componentOptions.component_share_enable_embed_clipboard === 'boolean'
      ? componentOptions.component_share_enable_embed_clipboard
      : true;
  const headerLayout = componentOptions.component_game_player_header_style || GameHeaderLayoutEnum.FULL;
  const layoutAreas = /* componentOptions.component_game_player_areas || */ [
    GameLayoutAreaEnum.GAME,
    GameLayoutAreaEnum.DESCRIPTION,
    GameLayoutAreaEnum.ADVERTISEMENT,
    GameLayoutAreaEnum.COLLECTION_POPULAR,
    GameLayoutAreaEnum.CATEGORY,
    GameLayoutAreaEnum.COLLECTION_LATEST,
  ];
  const cardType = componentOptions?.component_game_carousel_card_type;
  const startPlayButtonBackgroundColor = componentOptions?.component_game_mobile_play_button_background_color;
  const startPlayButtonColor = componentOptions?.component_game_mobile_play_button_color;

  const banners = ['idhb-middle-728'];
  const titleRef = React.useRef<HTMLInputElement | null>(null);
  const subtitleRef = React.useRef<HTMLInputElement | null>(null);
  const descriptionRef = React.useRef<HTMLInputElement | null>(null);
  const buttonMobilePlayRef = React.useRef<HTMLButtonElement | null>(null);
  const buttonMobileShareRef = React.useRef<HTMLButtonElement | null>(null);
  const buttonMobileClipBoardRef = React.useRef<HTMLButtonElement | null>(null);
  const buttonMoreRef = React.useRef<HTMLButtonElement | null>(null);

  const toggleTruncateDescription = () => {
    setDescriptionIsTruncated(!descriptionIsTruncated);
    if (descriptionIsTruncated) {
      setDescription(game?.description || '');
    } else {
      setDescription(truncateString(game?.description || '', descriptionTruncateLength, true));
    }
  };

  const handleToggleGame = () => {
    setGameRunning((running) => !running);
  };

  const openSnackbar = (value: 'share' | 'embed') => {
    if (value === 'share') {
      if (navigator.share) {
        navigator.share({
          url: window.location?.href,
        });
      } else {
        console.error("Browser doesn't support Web Share API");
      }
    } else {
      navigator.clipboard.writeText(game.src || '');
      // Todo: HUZ2ERA-1108 - Enable snackbar
      // if (snackbar) snackbar({ message: 'Copied to clipboard', variant: 'success' });
    }
  };

  React.useEffect(() => {
    adsRequest(banners);
    setDomLoaded(true);
  }, []);

  React.useEffect(() => {
    if (isMobile && !gameRunning && router.query.slug) {
      setGameRunning(true);
    }
  }, [router.query.slug]);

  React.useEffect(() => {
    const { events } = router;
    const handleRouteChange = () => {
      adsRequest(banners);
    };

    events.on('routeChangeComplete', handleRouteChange);
    events.on('hashChangeComplete', handleRouteChange);
    return () => {
      events.off('routeChangeComplete', handleRouteChange);
      events.off('hashChangeComplete', handleRouteChange);
    };
  }, [router]);

  React.useEffect(() => {
    setDescription(truncateString(game.description || '', descriptionTruncateLength, true));
  }, [game]);

  const checkSectionVisibility = (sectionName: GameLayoutAreaEnum) => {
    const grids = layoutAreas.map((area: string) => `"${area}"`).join('\r\n');
    return grids.includes(sectionName);
  };

  return (
    <Container maxWidth="md" disableGutters={true} ref={ref} {...useEditorHandlers('Game Page')}>
      <Box sx={{ gridArea: 'breadcrumbs' }}>
        <Container maxWidth={false} disableGutters={md}>
          <BreadCrumbs breadcrumbs={pageContext.breadcrumbs} />
        </Container>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gridRowGap: '32px',
          gridTemplateAreas: layoutAreas.map((area: GameLayoutAreaEnum) => `"${area}"`).join('\r\n'),
        }}
      >
        {domLoaded && (
          <Box sx={{ gridArea: GameLayoutAreaEnum.GAME, minWidth: 0 }}>
            <BrowserView>
              <Container maxWidth={false} disableGutters={md}>
                <GameDesktop elevation={1}>
                  {headerLayout !== GameHeaderLayoutEnum.HIDDEN && (
                    <GameHeader headerLayout={headerLayout}>
                      <GameHeaderImage headerLayout={headerLayout}>
                        <ImageLoaderWrapper
                          src={`${assetUrl}${game.images.cover1x1 || game.images.cover}/`}
                          width={headerLayout === GameHeaderLayoutEnum.FULL ? 64 : 32}
                          height={headerLayout === GameHeaderLayoutEnum.FULL ? 64 : 32}
                          alt={`${game.name}`}
                          priority={true}
                          layout="responsive"
                        />
                      </GameHeaderImage>
                      <GameHeaderMeta>
                        <Typography ref={titleRef} variant="h1" component="h1">
                          {game.name}
                        </Typography>
                        {headerLayout === GameHeaderLayoutEnum.FULL && (
                          <Typography ref={subtitleRef} variant="subtitle1" component="span">
                            {game.description}
                          </Typography>
                        )}
                      </GameHeaderMeta>
                    </GameHeader>
                  )}
                  <Box
                    sx={{
                      aspectRatio: '16/9',
                      backgroundColor: theme.palette.grey['900'],
                    }}
                  >
                    <GameFrame game={game} start={true} />
                  </Box>
                </GameDesktop>
              </Container>
            </BrowserView>
            <MobileView>
              <Container maxWidth={false} disableGutters={md}>
                <GameFrame
                  game={game}
                  start={gameRunning}
                  onStop={handleToggleGame}
                  enableOrientationLock={true}
                  enableDraggableMenu={true}
                  enableResponsive={true}
                />
                <GameMobile elevation={1}>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <Box sx={{ display: 'flex' }}>
                      <ImageLoaderWrapper
                        src={`${assetUrl}${game.images.cover1x1 || game.images.cover}/`}
                        width={100}
                        height={100}
                        alt={`${game.name}`}
                        priority={true}
                      />
                    </Box>
                    <GameHeaderMeta>
                      <Typography ref={titleRef} variant="h1" component="h1">
                        {game.name}
                      </Typography>
                      <Button
                        ref={buttonMobilePlayRef}
                        onClick={handleToggleGame}
                        variant="contained"
                        sx={{
                          marginTop: 'auto',
                          background: startPlayButtonBackgroundColor,
                          color: startPlayButtonColor,
                        }}
                      >
                        {t('button.continue_playing', 'Start playing')}
                      </Button>
                    </GameHeaderMeta>
                  </div>
                  {(enableShare || enableClipBoard) && (
                    <div style={{ padding: theme.spacing(2, 0, 0) }}>
                      {enableShare && (
                        <Button
                          ref={buttonMobileShareRef}
                          variant="contained"
                          size="small"
                          color="secondary"
                          onClick={() => openSnackbar('share')}
                          style={{
                            marginRight: theme.spacing(1),
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          }}
                          startIcon={<ShareIcon />}
                        >
                          {t('button.share', 'Share')}
                        </Button>
                      )}
                      {enableClipBoard && (
                        <Button
                          ref={buttonMobileClipBoardRef}
                          variant="contained"
                          size="small"
                          color="secondary"
                          onClick={() => openSnackbar('embed')}
                          style={{
                            marginRight: theme.spacing(1),
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          }}
                          startIcon={<CodeIcon />}
                        >
                          {t('button.embed', 'Copy link')}
                        </Button>
                      )}
                    </div>
                  )}
                </GameMobile>
              </Container>
            </MobileView>
          </Box>
        )}
        {!componentOptions.component_ads_disabled && !componentOptions.component_game_ads_middle_disabled && (
          <Box sx={{ gridArea: GameLayoutAreaEnum.ADVERTISEMENT, minWidth: 0 }}>
            <AdvertisementSlotContainer elevation={0}>
              <AdvertisementSlotStyled
                slotIdentifier={
                  layout === LayoutTypeEnum.AGGRESSIVE
                    ? componentOptions.aggressive_game_middle_id
                    : layout === LayoutTypeEnum.MODERATE
                    ? componentOptions.moderate_game_middle_id
                    : layout === LayoutTypeEnum.CONSERVATIVE
                    ? componentOptions.conservative_game_middle_id
                    : ''
                }
                variant={md ? '970x250' : '320x50'}
                elevation={1}
              />
            </AdvertisementSlotContainer>
          </Box>
        )}
        <Box sx={{ gridArea: GameLayoutAreaEnum.DESCRIPTION, minWidth: 0 }}>
          {inView && checkSectionVisibility(GameLayoutAreaEnum.DESCRIPTION) && (
            <Container maxWidth={false} disableGutters={md}>
              <Section elevation={1}>
                <DescriptionTitle
                  variant={headerLayout === 'hidden' ? 'h1' : 'h2'}
                  component={headerLayout === 'hidden' ? 'h1' : 'span'}
                >
                  {game.name}
                </DescriptionTitle>
                <SectionDescription ref={descriptionRef} variant="body1" paragraph gutterBottom>
                  {description}
                  <br />
                  <div style={{ display: descriptionIsTruncated ? 'none' : 'block' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{pageContext?.seoContent}</ReactMarkdown>
                  </div>
                </SectionDescription>
                {game.description && (
                  <Button
                    ref={buttonMoreRef}
                    variant="contained"
                    onClick={toggleTruncateDescription}
                    aria-label={t('more_link', 'More')}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      minWidth: 0,
                      width: theme.spacing(5),
                      height: theme.spacing(5),
                      margin: 'auto',
                      borderRadius: '100%',
                      transform: 'translate(-50%, 50%)',
                    }}
                  >
                    {!descriptionIsTruncated ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  </Button>
                )}
              </Section>
            </Container>
          )}
        </Box>
        <Box sx={{ gridArea: GameLayoutAreaEnum.CATEGORY, minWidth: 0 }}>
          {game?.categories[0]?.slug && inView && checkSectionVisibility(GameLayoutAreaEnum.CATEGORY) && (
            <CollectionSection>
              <Title component="h3" variant="h3">
                {t('category', game?.categories[0]?.slug)}
              </Title>
              <CarouselList
                padding={theme.spacing(2)}
                items={global.category || []}
                cardOptions={{
                  width: componentOptions.component_card_width || 512,
                  height: componentOptions.component_card_height || 288,
                  sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                  layout: 'responsive',
                  template: cardType,
                }}
                {...swiperOptions}
              />
            </CollectionSection>
          )}
        </Box>
        <Box sx={{ gridArea: GameLayoutAreaEnum.COLLECTION_LATEST, minWidth: 0 }}>
          {inView && checkSectionVisibility(GameLayoutAreaEnum.COLLECTION_LATEST) && (
            <CollectionSection>
              <Title component="h3" variant="h3">
                {t('latest_header', 'Latest')}
              </Title>
              <CarouselList
                padding={theme.spacing(2)}
                items={global.latest || []}
                cardOptions={{
                  width: componentOptions.component_card_width || 512,
                  height: componentOptions.component_card_height || 288,
                  sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                  layout: 'responsive',
                  template: cardType,
                }}
                {...swiperOptions}
              />
            </CollectionSection>
          )}
        </Box>
        <Box sx={{ gridArea: GameLayoutAreaEnum.COLLECTION_POPULAR, minWidth: 0 }}>
          {inView &&
            checkSectionVisibility(GameLayoutAreaEnum.COLLECTION_POPULAR) &&
            Array.isArray(global.popular) &&
            global.popular.length > 0 && (
              <CollectionSection>
                <Title component="h3" variant="h3">
                  {t('popular_header', 'Popular')}
                </Title>
                <CarouselList
                  padding={theme.spacing(2)}
                  items={global.popular || []}
                  cardOptions={{
                    width: componentOptions.component_card_width || 512,
                    height: componentOptions.component_card_height || 288,
                    sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                    layout: 'responsive',
                    template: cardType,
                  }}
                  {...swiperOptions}
                />
              </CollectionSection>
            )}
        </Box>
      </Box>
    </Container>
  );
};
